// frontend/src/pages/MatchScoring/BallsStrip.js
//
// Shows the last over (or last few balls) for the live innings.
// We render balls of the highest over_number we see in recentBalls.
// If a wide/no-ball just occurred, we render it inline.
//
// onBallTap (optional): if provided, each ball becomes a tappable button
// that opens the EditBallModal in the parent.

import React from 'react';

function classify(b) {
  if (b.dismissal_type) return { cls: 'sc-wkt',  label: 'W' };
  if (b.wides   > 0) {
    const total = parseInt(b.total_runs, 10);
    return { cls: 'sc-extra', label: total === 1 ? 'Wd' : `${total}Wd` };
  }
  if (b.noballs > 0) {
    const total = parseInt(b.total_runs, 10);
    return { cls: 'sc-extra', label: total === 1 ? 'Nb' : `${total}Nb` };
  }
  if (b.byes    > 0) return { cls: 'sc-extra', label: `${b.byes}B` };
  if (b.legbyes > 0) return { cls: 'sc-extra', label: `${b.legbyes}Lb` };
  const r = parseInt(b.runs_off_bat || 0, 10);
  if (r === 0) return { cls: 'sc-dot', label: '·' };
  if (r === 4) return { cls: 'sc-four', label: '4' };
  if (r === 6) return { cls: 'sc-six',  label: '6' };
  return { cls: '', label: String(r) };
}

export default function BallsStrip({ recentBalls, membersById, onBallTap }) {
  if (!recentBalls || recentBalls.length === 0) {
    return (
      <div className="sc-balls-strip">
        <div className="sc-balls-label">
          <span>This over</span>
          <span className="sc-over-num">OVER 1</span>
        </div>
        <div className="sc-balls-list">
          <span style={{ color: 'var(--sc-text-faint)', fontSize: 12 }}>
            No balls yet — tap a number below to score
          </span>
        </div>
      </div>
    );
  }

  // Find max over_number — that's the live over. Show all balls with that over_number.
  const maxOver = recentBalls.reduce((m, b) => Math.max(m, b.over_number), 0);
  const thisOver = recentBalls.filter(b => b.over_number === maxOver);
  const bowlerId = thisOver[thisOver.length - 1]?.bowler_id;
  const bowlerName = bowlerId ? membersById.get(bowlerId)?.display_name || '' : '';

  return (
    <div className="sc-balls-strip">
      <div className="sc-balls-label">
        <span>
          This over{bowlerName ? ` · ${bowlerName.split(' ')[0]}` : ''}
          {onBallTap && <span style={{ marginLeft: 6, color: 'var(--sc-text-faint)' }}>· tap to edit</span>}
        </span>
        <span className="sc-over-num">OVER {maxOver + 1}</span>
      </div>
      <div className="sc-balls-list">
        {thisOver.map(b => {
          const { cls, label } = classify(b);
          if (onBallTap) {
            return (
              <button
                key={b.id}
                type="button"
                className={`sc-ball ${cls}`}
                onClick={() => onBallTap(b)}
                style={{ cursor: 'pointer', font: 'inherit', padding: 0 }}
                title="Tap to edit this ball"
              >
                {label}
              </button>
            );
          }
          return <div key={b.id} className={`sc-ball ${cls}`}>{label}</div>;
        })}
      </div>
    </div>
  );
}
